import React, { useEffect, useState } from "react";
import { DashboardShell } from "@/components/yc/DashboardShell";
import { api } from "@/lib/api";
import { specLabel } from "@/lib/specialisms";

const Matching = () => {
    const [campaigns, setCampaigns] = useState([]);
    const [active, setActive] = useState(null);
    const [shortlist, setShortlist] = useState([]);
    const [matchId, setMatchId] = useState(null);
    const [selectedCreator, setSelectedCreator] = useState(null);
    const [reasoning, setReasoning] = useState("");

    const load = () => api.get("/admin/open-campaigns").then((r) => setCampaigns(r.data));
    useEffect(() => { load(); }, []);

    const openCampaign = async (c) => {
        setActive(c); setShortlist([]); setMatchId(null); setSelectedCreator(null); setReasoning("");
        const r = await api.post(`/admin/campaign/${c.campaign_id}/shortlist`);
        setShortlist(r.data.shortlist);
        setMatchId(r.data.match_id);
    };

    const confirm = async () => {
        if (!matchId || !selectedCreator) return;
        const rank = shortlist.find((e) => e.creator_id === selectedCreator.creator_id)?.rank || 0;
        await api.post(`/admin/match/${matchId}/confirm`, {
            creator_id: selectedCreator.creator_id,
            strategist_reasoning: reasoning,
            algorithm_rank: rank,
        });
        await load();
        setActive(null); setShortlist([]); setMatchId(null); setSelectedCreator(null); setReasoning("");
    };

    return (
        <DashboardShell role="admin" title="Matching" subtitle="Ranked shortlists, human-confirmed">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="yc-card-sm p-4 lg:col-span-1">
                    <h3 className="font-heading text-lg font-bold">Open campaigns ({campaigns.length})</h3>
                    <div className="mt-4 space-y-2">
                        {campaigns.map((c) => (
                            <button key={c.campaign_id} onClick={() => openCampaign(c)} data-testid={`match-camp-${c.campaign_id}`}
                                className={`w-full text-left p-3 rounded-xl border-2 border-yc-ink transition-all ${active?.campaign_id === c.campaign_id ? "bg-yc-blue text-white" : "bg-white"}`}>
                                <div className="font-semibold">{c.title}</div>
                                <div className="text-xs opacity-75">{c.package_id} package</div>
                            </button>
                        ))}
                        {campaigns.length === 0 && <div className="text-center text-yc-ink/60 py-6 text-sm">Nothing open.</div>}
                    </div>
                </div>

                {active && (
                    <div className="lg:col-span-2 space-y-4">
                        <div className="yc-card p-6">
                            <div className="yc-overline">Brief</div>
                            <h2 className="font-heading text-2xl font-bold mt-1">{active.title}</h2>
                            <p className="text-sm text-yc-ink/80 mt-2">{active.brief}</p>
                        </div>

                        <div className="space-y-3">
                            {shortlist.length === 0 && <div className="text-yc-ink/60 text-sm">Generating shortlist…</div>}
                            {shortlist.map((e) => (
                                <button data-testid={`shortlist-${e.creator_id}`} key={e.creator_id} onClick={() => setSelectedCreator(e.creator)} className={`w-full text-left yc-card p-5 transition-all ${selectedCreator?.creator_id === e.creator_id ? "ring-4 ring-yc-blue/30 border-yc-blue" : ""}`}>
                                    <div className="flex items-start justify-between gap-3">
                                        <div>
                                            <div className="flex items-center gap-3">
                                                <div className="font-heading font-black text-3xl">#{e.rank}</div>
                                                <div>
                                                    <div className="font-heading text-xl font-bold">{e.creator?.name || "Unknown"}</div>
                                                    <div className="text-xs text-yc-ink/60">{(e.creator?.specialisms || []).map(specLabel).join(", ")}</div>
                                                </div>
                                            </div>
                                            <ul className="mt-3 text-sm text-yc-ink/80 list-disc list-inside space-y-1">
                                                {e.reasons.map((r, i) => <li key={`reason-${i}`}>{r}</li>)}
                                            </ul>
                                        </div>
                                        <div className="text-right">
                                            <div className="yc-overline">Score</div>
                                            <div className="font-heading font-black text-3xl text-yc-blue">{e.score}</div>
                                        </div>
                                    </div>
                                </button>
                            ))}
                        </div>

                        {selectedCreator && (
                            <div className="yc-card p-6">
                                <div className="yc-overline">Strategist reasoning</div>
                                <textarea data-testid="strategist-reasoning" rows={3} placeholder="Why this creator over the others?" className="yc-input mt-2" value={reasoning} onChange={(e) => setReasoning(e.target.value)} />
                                <button data-testid="confirm-match" onClick={confirm} disabled={!reasoning.trim()} className="yc-btn-primary mt-4">Confirm match with {selectedCreator.name}</button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </DashboardShell>
    );
};

export default Matching;
